import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Calendar, Plus, ArrowLeftRight, Check, X, Clock,
  Briefcase, Baby, Palmtree, Stethoscope
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const LEAVE_TYPES = [
  { value: 'sick', label: 'Sick Leave', icon: Stethoscope, color: '#EF4444' },
  { value: 'vacation', label: 'Vacation', icon: Palmtree, color: '#10B981' },
  { value: 'paternity', label: 'Paternity Leave', icon: Baby, color: '#4B167A' },
  { value: 'maternity', label: 'Maternity Leave', icon: Baby, color: '#D4AF37' }
];

export default function HRPage() {
  const { api, user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [shiftSwaps, setShiftSwaps] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal States
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  
  // Form Data
  const [leaveData, setLeaveData] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  const [swapData, setSwapData] = useState({
    original_date: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leaveRes, marketplaceRes] = await Promise.all([
        api.get('/hr/leave/requests'),
        api.get('/hr/shift-swap/marketplace')
      ]);
      setLeaveRequests(leaveRes.data);
      setMarketplace(marketplaceRes.data);
    } catch (error) {
      console.error('Failed to fetch HR data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLeaveSubmit = async () => {
    if (!leaveData.leave_type || !leaveData.start_date || !leaveData.end_date || !leaveData.reason) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await api.post('/hr/leave/request', leaveData);
      toast.success('Leave request submitted');
      setShowLeaveModal(false);
      setLeaveData({ leave_type: '', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleSwapSubmit = async () => {
    if (!swapData.original_date) {
      toast.error('Please select a date');
      return;
    }

    try {
      await api.post('/hr/shift-swap/request', swapData);
      toast.success('Shift posted to marketplace');
      setShowSwapModal(false);
      setSwapData({ original_date: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post shift');
    }
  };

  const handleAcceptSwap = async (swapId) => {
    try {
      await api.post(`/hr/shift-swap/accept/${swapId}`);
      toast.success('Shift swap accepted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept swap');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      approved: 'bg-[#10B981]/20 text-[#10B981]',
      rejected: 'bg-[#EF4444]/20 text-[#EF4444]',
      open: 'bg-[#D4AF37]/20 text-[#D4AF37]',
      pending_approval: 'bg-[#4B167A]/40 text-white'
    };
    return styles[status] || 'bg-white/10 text-white';
  };

  const getLeaveTypeInfo = (type) => {
    return LEAVE_TYPES.find(lt => lt.value === type) || LEAVE_TYPES[0];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="hr-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              HR <span className="text-[#D4AF37]">Suite</span>
            </h1>
            <p className="text-white/50 mt-1">Manage leave requests and shift swaps</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowLeaveModal(true)}
              className="bg-[#D4AF37] text-[#0A091E] hover:bg-[#D4AF37]/80"
              data-testid="btn-request-leave"
            >
              <Plus className="w-4 h-4 mr-2" />
              Request Leave
            </Button>
            <Button
              onClick={() => setShowSwapModal(true)}
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="btn-post-swap"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              Post Shift
            </Button>
          </div>
        </div>

        <Tabs defaultValue="leave" className="space-y-6">
          <TabsList className="bg-[#1F1B71]/20 border border-white/10">
            <TabsTrigger 
              value="leave" 
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A091E]"
              data-testid="tab-leave"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Leave Requests
            </TabsTrigger>
            <TabsTrigger 
              value="marketplace"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A091E]"
              data-testid="tab-marketplace"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              Shift Marketplace
            </TabsTrigger>
          </TabsList>

          {/* Leave Requests Tab */}
          <TabsContent value="leave" className="space-y-4">
            {/* Leave Balance Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {LEAVE_TYPES.map((type) => (
                <Card key={type.value} className="glass-card border-0">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${type.color}20` }}
                      >
                        <type.icon className="w-5 h-5" style={{ color: type.color }} />
                      </div>
                      <div>
                        <p className="text-white/50 text-xs uppercase">{type.label}</p>
                        <p className="text-white font-semibold">
                          {leaveRequests.filter(r => r.leave_type === type.value && r.status === 'approved').length} used
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Leave Requests List */}
            <Card className="glass-card border-0">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-white">Your Leave Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-white/50">Loading...</div>
                ) : leaveRequests.length === 0 ? (
                  <div className="p-8 text-center text-white/50">
                    No leave requests found
                  </div>
                ) : (
                  <div className="divide-y divide-white/10">
                    {leaveRequests.map((request) => {
                      const typeInfo = getLeaveTypeInfo(request.leave_type);
                      return (
                        <div key={request.id} className="p-4 flex items-center justify-between" data-testid={`leave-${request.id}`}>
                          <div className="flex items-center gap-4">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${typeInfo.color}20` }}
                            >
                              <typeInfo.icon className="w-5 h-5" style={{ color: typeInfo.color }} />
                            </div>
                            <div>
                              <p className="font-medium text-white">{typeInfo.label}</p>
                              <p className="text-sm text-white/50">
                                {formatDate(request.start_date)} - {formatDate(request.end_date)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={getStatusBadge(request.status)}>
                              {request.status}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shift Marketplace Tab */}
          <TabsContent value="marketplace" className="space-y-4">
            <Card className="glass-card border-0">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-white">Available Shifts</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-white/50">Loading...</div>
                ) : marketplace.length === 0 ? (
                  <div className="p-8 text-center text-white/50">
                    No shifts available in marketplace
                  </div>
                ) : (
                  <div className="divide-y divide-white/10">
                    {marketplace.map((swap) => (
                      <div key={swap.id} className="p-4 flex items-center justify-between" data-testid={`swap-${swap.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {formatDate(swap.original_date)}
                            </p>
                            {swap.notes && (
                              <p className="text-sm text-white/50">{swap.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusBadge(swap.status)}>
                            {swap.status}
                          </Badge>
                          {swap.status === 'open' && swap.requester_id !== user?.id && (
                            <Button
                              size="sm"
                              onClick={() => handleAcceptSwap(swap.id)}
                              className="bg-[#10B981] hover:bg-[#10B981]/80 text-white"
                              data-testid={`btn-accept-swap-${swap.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Leave Request Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Request Leave
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Leave Type</Label>
              <Select
                value={leaveData.leave_type}
                onValueChange={(value) => setLeaveData({ ...leaveData, leave_type: value })}
              >
                <SelectTrigger className="bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-leave-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  {LEAVE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Start Date</Label>
                <Input
                  type="date"
                  value={leaveData.start_date}
                  onChange={(e) => setLeaveData({ ...leaveData, start_date: e.target.value })}
                  className="bg-[#0A091E]/50 border-white/20 text-white"
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">End Date</Label>
                <Input
                  type="date"
                  value={leaveData.end_date}
                  onChange={(e) => setLeaveData({ ...leaveData, end_date: e.target.value })}
                  className="bg-[#0A091E]/50 border-white/20 text-white"
                  data-testid="input-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Reason</Label>
              <Textarea
                value={leaveData.reason}
                onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })}
                placeholder="Explain your reason..."
                className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                data-testid="input-leave-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLeaveModal(false)} className="text-white/70">
              Cancel
            </Button>
            <Button onClick={handleLeaveSubmit} className="bg-[#D4AF37] text-[#0A091E]" data-testid="btn-submit-leave">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Swap Modal */}
      <Dialog open={showSwapModal} onOpenChange={setShowSwapModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#D4AF37]" />
              Post Shift to Marketplace
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Shift Date</Label>
              <Input
                type="date"
                value={swapData.original_date}
                onChange={(e) => setSwapData({ ...swapData, original_date: e.target.value })}
                className="bg-[#0A091E]/50 border-white/20 text-white"
                data-testid="input-swap-date"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Notes (Optional)</Label>
              <Textarea
                value={swapData.notes}
                onChange={(e) => setSwapData({ ...swapData, notes: e.target.value })}
                placeholder="Any additional notes..."
                className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                data-testid="input-swap-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSwapModal(false)} className="text-white/70">
              Cancel
            </Button>
            <Button onClick={handleSwapSubmit} className="bg-[#D4AF37] text-[#0A091E]" data-testid="btn-submit-swap">
              Post Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
